import { GameEvent, User } from '@/types'
import { MessageAction, OutcomingMessage } from '../main-frame/message-types'

export type GetUserFunction = () => Promise<User | null>

export interface UserServiceOptions {
    getUser: GetUserFunction
    authorizeUser: GetUserFunction
}

type ValueType<T> = T extends Promise<infer U> ? U : T

const unauthorized = {
    action: MessageAction.userInfo,
    type: 'user' as OutcomingMessage['type'],
    payload: {
        name: null,
        id: null,
        photos: [],
        isAuthorized: false,
    },
}

const convertUserToMessage = ({ _id, firstName, lastName, avatar, goldFenecBalance }: User) => ({
    action: MessageAction.userInfo,
    type: 'user' as OutcomingMessage['type'],
    payload: {
        name: `${firstName} ${lastName}`,
        id: _id,
        photos: [avatar],
        goldBalance: goldFenecBalance,
        isAuthorized: true,
    },
})

const attachEventToMessage = (event: GameEvent, message: Omit<OutcomingMessage, 'originalMessageEvent'>) => ({
    ...message,
    originalMessageEvent: event as GameEvent,
})

export class UserService {
    private getUserFromService: UserServiceOptions['getUser']

    private authorizeUser: UserServiceOptions['authorizeUser']

    constructor(options: UserServiceOptions) {
        this.getUserFromService = options.getUser
        this.authorizeUser = options.authorizeUser
    }

    async getUser() {
        const user = await this.getUserFromService()

        if (!user) {
            return unauthorized
        }

        return convertUserToMessage(user)
    }

    async handleMessage(event: GameEvent, onLogin: () => void) {
        let userInfo: ValueType<ReturnType<typeof this.getUser>>
        switch (event.data.action) {
            case MessageAction.getUser:
                return attachEventToMessage(event, await this.getUser())

            case MessageAction.authorizeUser:
                userInfo = await this.getUser()
                if (!userInfo.payload.isAuthorized) {
                    try {
                        const userData = await this.authorizeUser()
                        userInfo = userData ? convertUserToMessage(userData) : unauthorized
                        if (userData) {
                            onLogin()
                        }
                    } catch (e) {
                        console.info(e)
                        userInfo = unauthorized
                    }
                }

                return attachEventToMessage(event, userInfo)

            default:
                return attachEventToMessage(event, {
                    action: MessageAction.unknownMessageType,
                    payload: {
                        error: `Unknown message action "${event.data.action}"`,
                    },
                    type: 'error',
                })
        }
    }
}
